import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Switch,
  Stack,
  Paper,
  FormControlLabel,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Add, Tune, Close } from '@mui/icons-material';
import {
  menuPalette,
  parseBRLInput,
  formatBRLDisplay,
  toNum,
} from '../utils/dashboardHelpers';

const emptyCompForm = { id: null, nome: '', preco: 0, precoInput: '', ativo: true };

const ComplementosPanel = ({
  complementos,
  compForm,
  setCompForm,
  handleSaveComplemento,
  handleDeleteComplemento,
}) => (
  <Paper
    sx={{
      mb: 3,
      p: { xs: 2, md: 3 },
      border: `1.5px dashed ${menuPalette.border}`,
      backgroundColor: 'rgba(255,255,255,0.95)',
    }}
  >
    <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.04em', color: menuPalette.ink }}>
        Complementos
      </Typography>
      <Chip
        size="small"
        icon={<Tune sx={{ fontSize: '0.9rem !important' }} />}
        label={`${(complementos || []).length} cadastrado(s)`}
        variant="outlined"
        sx={{ fontWeight: 700, color: menuPalette.blue, borderColor: menuPalette.border, backgroundColor: 'rgba(31,79,191,0.05)', '& .MuiChip-icon': { color: menuPalette.blue } }}
      />
    </Box>
    <Typography variant="body2" color="text.secondary" mb={1.5}>
      Cadastre aqui e, ao editar um item, selecione os complementos e defina regras (obrigatório, mínimo, máximo).
    </Typography>

    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2.5,
        borderColor: menuPalette.border,
        bgcolor: 'rgba(255,255,255,0.95)',
        borderLeft: `4px solid ${compForm.id ? menuPalette.orange : menuPalette.blue}`,
        transition: 'border-color .2s ease',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
        <Chip
          size="small"
          icon={compForm.id ? <Edit sx={{ fontSize: '0.9rem !important' }} /> : <Add sx={{ fontSize: '0.95rem !important' }} />}
          label={compForm.id ? 'Editando complemento' : 'Novo complemento'}
          color={compForm.id ? 'secondary' : 'primary'}
          sx={{ fontWeight: 700 }}
        />
        {compForm.id && compForm.nome && (
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            {compForm.nome}
          </Typography>
        )}
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Nome"
          value={compForm.nome}
          onChange={(e) => setCompForm((s) => ({ ...s, nome: e.target.value }))}
          fullWidth
        />
        <TextField
          label="Preço"
          value={compForm.precoInput ?? ''}
          onChange={(e) => {
            const { display, numeric } = parseBRLInput(e.target.value);
            setCompForm((s) => ({ ...s, precoInput: display, preco: numeric }));
          }}
          sx={{ width: { xs: '100%', md: 160 } }}
          inputProps={{ inputMode: 'numeric' }}
        />
        <Tooltip title="Quando ativo, o complemento fica disponível para uso nos itens">
          <FormControlLabel
            control={
              <Switch
                checked={!!compForm.ativo}
                onChange={(e) => setCompForm((s) => ({ ...s, ativo: e.target.checked }))}
              />
            }
            label="Ativo"
            sx={{ flexShrink: 0, m: 0 }}
          />
        </Tooltip>
      </Stack>
      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25} justifyContent="flex-end" alignItems={{ xs: 'stretch', sm: 'center' }} mt={2}>
        <Button
          variant="outlined"
          startIcon={<Close />}
          onClick={() => setCompForm({ ...emptyCompForm })}
          sx={{
            height: 44,
            px: 2.5,
            color: 'text.secondary',
            borderColor: 'rgba(28,39,76,0.22)',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              borderColor: menuPalette.ink,
              backgroundColor: 'rgba(28,39,76,0.05)',
            },
          }}
        >
          {compForm.id ? 'Cancelar edição' : 'Limpar'}
        </Button>
        <Button
          variant="contained"
          color={compForm.id ? 'secondary' : 'primary'}
          startIcon={compForm.id ? <Edit /> : <Add />}
          onClick={handleSaveComplemento}
          sx={{
            height: 44,
            px: 3,
            boxShadow: compForm.id
              ? '0 10px 22px rgba(242,107,58,0.32)'
              : '0 10px 22px rgba(31,79,191,0.28)',
            '&:hover': { transform: 'translateY(-1px)' },
            transition: 'transform .15s ease, box-shadow .15s ease',
          }}
        >
          {compForm.id ? 'Salvar alterações' : 'Adicionar complemento'}
        </Button>
      </Stack>
    </Paper>

    {(complementos || []).length === 0 ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 3, color: 'text.secondary' }}>
        <Tune sx={{ color: menuPalette.border, fontSize: '1.8rem' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Nenhum complemento cadastrado ainda</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>Use o formulário acima para criar o primeiro.</Typography>
      </Box>
    ) : (
      <Stack spacing={1}>
        {(complementos || []).map((c) => {
          const inactive = c.ativo === false;
          const isEditing = compForm.id === c.id;
          return (
            <Paper
              key={c.id}
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                borderColor: isEditing ? menuPalette.orange : menuPalette.border,
                borderWidth: isEditing ? 2 : 1,
                bgcolor: inactive ? 'rgba(243,243,246,0.7)' : 'rgba(255,255,255,0.95)',
                opacity: inactive ? 0.72 : 1,
                transition: 'all .2s ease',
                '&:hover .comp-actions': { opacity: 1 },
              }}
            >
              <Box flex={1} minWidth={0}>
                <Typography sx={{ fontWeight: 700, color: menuPalette.ink, textDecoration: inactive ? 'line-through' : 'none', textDecorationColor: 'rgba(28,39,76,0.35)' }}>
                  {c.nome || 'Complemento'}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.preco || 0)}
                sx={{ fontWeight: 800, color: menuPalette.orange, borderColor: menuPalette.categoriaBorder, backgroundColor: 'rgba(242,107,58,0.06)' }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={inactive ? 'Inativo' : 'Ativo'}
                color={inactive ? 'default' : 'success'}
                variant={inactive ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700 }}
              />
              <Box className="comp-actions" sx={{ display: 'flex', transition: 'opacity .2s ease', opacity: { xs: 1, md: 0.5 } }}>
                <Tooltip title="Editar complemento">
                  <IconButton
                    size="small"
                    aria-label="Editar complemento"
                    onClick={() =>
                      setCompForm({
                        id: c.id,
                        nome: c.nome,
                        preco: toNum(c.preco),
                        precoInput: c.preco != null ? formatBRLDisplay(c.preco) : '',
                        ativo: c.ativo !== false,
                      })
                    }
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir complemento">
                  <IconButton size="small" color="error" aria-label="Excluir complemento" onClick={() => handleDeleteComplemento(c.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    )}
  </Paper>
);

export default ComplementosPanel;
